<template>
  <div class="checkout-summary">
    <LoadingState v-if="loading" />
    <template v-else>
      <h3 class="text-primary text-uppercase d-flex">
        <Logo v-if="logo" class="m-2" :logo="logo" :size="30" />
        <span class="m-2">{{ title }}</span>
      </h3>
      <ul class="checkout-summary-list">
        <!-- package -->
        <li class="item" v-for="(item, index) in getLineItems()" :key="`line-item-${index}`">
          <span class="text-secondary">{{ item.title }}:</span>
          <span>{{ item.data }}</span>
        </li>

        <!-- store builder -->
        <!-- <li class="item" v-if="storebuilder">
          <span class="text-secondary">Store Builder Package:</span>
          <span class="text-right">{{ storebuilder.name }}</span>
        </li> -->

        <!-- addons -->
        <template v-if="showAddons">
          <li class="item" v-for="(item, index) in getAddons" :key="`addon-${index}`">
            <span class="text-secondary">{{ item.title }}</span>
            <span>{{ item.data }}</span>
          </li>
        </template>

        <!-- sales tax -->
        <li class="item">
          <span class="text-secondary">{{ $t("global.sales-tax") }}</span>
          <span>{{ `+ ${moneyFormat(tax)}` }}</span>
        </li>

        <!-- coupon/discount -->
        <li v-if="coupon" class="item">
          <span class="text-secondary">{{ $t("global.coupon-code") }}: {{ coupon }}</span>
          <span>{{ `- ${moneyFormat(discount)}` }}</span>
        </li>
      </ul>
      <div class="d-flex justify-content-between mt-5">
        <h3 class="text-secondary">{{ $t("global.total") }}</h3>
        <span class="text-primary h3">
          {{ moneyFormat(total) }} <span class="text-capitalize" v-if="showTerm">{{ term }}</span>
        </span>
      </div>
    </template>
  </div>
</template>

<script>
import { computed } from "@vue/composition-api";
import { fromGBFormat } from "leatherman-js/utils/formatters/size";
import LoadingState from "@/components/display/LoadingState";
import Logo from "@/components/display/Logo";
import { moneyFormat } from "leatherman-js/utils/formatters/money";

export default {
  name: "CheckoutSummary",
  components: {
    Logo,
    LoadingState,
  },
  props: {
    package: { type: Object },
    storebuilder: { type: Boolean, default: false },
    addons: { type: Array, default: () => [] },
    logo: { type: String },
    title: { type: String },
    appName: { type: String },
    term: { type: String, default: "monthly", required: true },
    showTerm: { type: Boolean, default: true },
    location: Object,
    hostname: String,
    nickname: String,
    coupon: String,
    discount: Number,
    tax: Number,
    total: Number,
    loading: Boolean,
  },
  setup(props, { root }) {
    const getLineItems = () => {
      let list = [];

      if (!props.storebuilder) {
        list = [
          ...list,
          {
            title: root.$t("global.data-center"),
            data: props.location?.name || root.$t("global.none-selected"),
          },
          {
            title: root.$t("global.plan-nickname"),
            data: props.nickname || root.$t("global.none-selected"),
          },
          {
            title: root.$t("global.sites"),
            data: props.package.allottedSites,
          },
          {
            title: root.$t("global.email-accounts"),
            data: root.$t("global.unlimited"),
          },
          {
            title: root.$t("global.disk-space"),
            data: `${fromGBFormat(props.package.diskSpace)}`,
          },
          {
            title: root.$t("global.bandwidth"),
            data: `${fromGBFormat(props.package.bandwidth)}`,
          },
        ];
      }

      if (props.package) {
        const price = () => {
          if (props.term === "monthly") {
            return props.package.monthlyFee + props.package.appPricing[props.package.app.id];
          } else {
            return props.package.annualFee;
          }
        };

        list = [
          ...list,
          {
            title: root.$t("global.backups"),
            data: root.$t("global.days", { count: 30 }),
          },

          {
            title: root.$t("global.price"),
            data: `${moneyFormat(price())} ${root.$t(`global.${props.term}`)}`,
          },
        ];
      }

      return list;
    };
    const showAddons = computed(() => !!props.addons.length);
    const getAddons = computed(() =>
      props.addons.map((addon) => {
        const price = props.term === "monthly" ? addon.monthlyFee : addon.annualFee;
        return {
          title: addon.name,
          data: `+ ${moneyFormat(price)}`,
        };
      })
    );

    return {
      getLineItems,
      getAddons,
      moneyFormat,
      showAddons,
    };
  },
};
</script>
<style lang="scss" scoped>
@import "~@/styles/variables";
</style>
